'use client'

import { useRouter } from 'next/navigation'
import { createContext, useContext, useEffect, useState } from 'react'
import useSWR from 'swr'

import { useSupabase } from './supabase-provider'
import { updateUserByadmin } from '../../functions'

const Context = createContext({
  user: null,
  session: null,
  error: null,
  isLoading: false,
  mutate: null,
  emailExists: async () => {},
  passwordReset: async () => {},
  updatePassword: async () => {},
  signOut: async () => {},
  signUp: async () => {},
  signInWithGoogle: async () => {},
  signInWithGoogleForVote: async () => {},
  signInWithEmailPassword: async () => {},
  addMessageToChat: async () => {},
  getMessageForChat: async () => {},
  createNewChat: async () => {},
  createNewChatAndSendMessage: async () => {},
  addPeopleToChat: async () => {},
  getSpecificRoomForUser: async () => {},
  getCompanyUserData: async () => {},
  getImageAndNameOfUser: async () => {},
  renameChat: async () => {},
  usersInRoom: async () => {},
  deletePeopleInChat: async () => {},
  deleteMessage: async () => {},
  editMessage: async () => {},
  getPublicAvatarUrl: async () => {},
})

export default function SupabaseAuthProvider({ serverSession, children }) {
  const [currentProgram, setCurrentProgram] = useState('')
  const [admin_city, setAdminCity] = useState('london')
  const [navigationsItems, setNavigationsItems] = useState([])
  const [selectedCompany, setSelectedCompany] = useState(null)
  const [allCompanyInfos, setAllCompanyInfos] = useState(null)
  const [isCompany, setISCompany] = useState(null)
  const [iSCoreProgram, setISCoreProgram] = useState(null)
  const [isFirepit, setISFirepit] = useState(null)
  const [isAlumni, setISAlumni] = useState(null)
  const [isInterview, setISInterview] = useState(null)
  const [isFurnance, setISFurnance] = useState(null)
  const [selectedCompanyID, setSelectedCompanyID] = useState(null)
  const [stepContext, setStepContext] = useState(1)
  const [joinedTeams, setJoinedTeams] = useState([])
  const [roomId, setRoomId] = useState(null)
  const [roomNameNew, setRoomName] = useState('')
  const [isNewUpdate, setIsisNewUpdate] = useState(false)
  const [avatar_url, setAvatar] = useState(null)
  const [isUserCookie, setUserCookie] = useState(null)
  const [inboxNav, setInboxNav] = useState([])
  const { supabase } = useSupabase()
  const router = useRouter()

  useEffect(() => {
    // Load user from localStorage on initial render
    const storedCompanyID = localStorage.getItem('selectedCompanyID')
    if (storedCompanyID) {
      setSelectedCompanyID(JSON.parse(storedCompanyID))
    }
  }, [])

  useEffect(() => {
    // Save user to localStorage whenever it changes
    if (selectedCompanyID) {
      localStorage.setItem(
        'selectedCompanyID',
        JSON.stringify(selectedCompanyID),
      )
    } else {
      localStorage.removeItem('selectedCompanyID')
    }
  }, [selectedCompanyID])
  const getURL = (path) => {
    let url =
      process?.env?.NEXT_PUBLIC_MAIN_URL ?? // Set this to your site URL in production env.
      process?.env?.NEXT_PUBLIC_VERCEL_URL ?? // Automatically set by Vercel.
      'http://localhost:3000/'
    // Make sure to include `https://` when not localhost.
    url = url.includes('http') ? url : `https://${url}`
    // Make sure to include a trailing `/`.
    url = url.charAt(url.length - 1) === '/' ? url : `${url}/`
    return url + path
  }
  //  Sign Up
  const signUp = async ({
    email,
    password,
    emailRedirectTo,
    role,
    voterCompany,
    roleName,
    name,
  }) => {
    const signUpData = {
      email,
      password,
      options: {
        emailRedirectTo,
      },
    }

    const { data, error } = await supabase.auth.signUp(signUpData)
    if (error) return { error }
    const userId = data?.user?.id
    if (role && userId) {
      await updateUserByadmin(userId, { app_metadata: { role } }, false)
      await supabase.auth.refreshSession()
      let obj = { role }
      if (voterCompany) {
        obj.voterCompany = voterCompany
      }
      if (roleName) {
        obj.voterRoleName = roleName
      }
      if (name) {
        obj.name = name
      }
      await supabase.from('users').update(obj).eq('id', userId)
    }

    router.refresh()
    return { data }
  }

  const fetchSignedUrl = async (user) => {
    const path = `${user.id}.avatar_url`
    if (user?.id) {
      try {
        const { data, error } = await supabase.storage
          .from('profiles')
          .createSignedUrl(path, 60000)

        if (error) {
          return null
        }

        return data.signedUrl
      } catch (err) {
        // Handle unexpected errors
        return null
      }
    }
    return user
  }
  // Get USER
  const getUser = async () => {
    const { data: user, error } = await supabase
      .from('users')
      .select('*')
      .eq('id', serverSession?.user?.id)
      .single()

    if (error) {
      return null
    } else {
      await getUrl(user)
      return user
    }
  }

  const getUrl = async (user) => {
    if (!user) return null
    const url = await fetchSignedUrl(user)
    setAvatar(url)
    return url
  }
  const {
    data: user,
    error,
    isLoading,
    mutate,
  } = useSWR(serverSession ? 'profile-context' : null, getUser)

  // Sign Out
  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    if (error) {
      supabase.auth._removeSession()
      router.refresh()
    } else {
      router.refresh()
    }
  }
  const signInWithGoogle = async (role) => {
    try {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: role
            ? `${window.location.origin}/auth/callback?role=${role}`
            : `${window.location.origin}/auth/callback`,
        },
      })

      if (error) {
        console.error('Error during sign-in:', error)
        return { error }
      }
    } catch (err) {
      console.error('An unexpected error occurred:', err)
      return { error: err.message }
    }
  }

  const signInWithGoogleForVote = async (
    voteID,
    role,
    companyName,
    roleName,
  ) => {
    let redirectUrl = `${window.location.origin}/auth/vote?voteID=${voteID}`

    // Append role and companyName if they are not null or empty
    if (role) redirectUrl += `&role=${encodeURIComponent(role)}`
    if (companyName)
      redirectUrl += `&company=${encodeURIComponent(companyName)}`
    if (roleName) redirectUrl += `&roleName=${encodeURIComponent(roleName)}`

    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
      },
    })

    if (error) {
      console.error('Error during Google sign-in:', error)
    }

    return data
  }

  //   signInWithEmailPassword,
  const signInWithEmailPassword = async ({ email, password }) => {
    const res = await supabase.auth.signInWithPassword({
      email,
      password,
    })
    if (res.error) {
      return res
    } else {
      router.refresh
      return res
    }
  }

  async function getSpecificRoomForUser(userId) {
    try {
      // Fetch chat rooms where the user is the owner
      const { data: ownedChatRooms, error: ownedRoomsError } = await supabase
        .from('chat_room')
        .select('*')
        .eq('user_id', userId)

      if (ownedRoomsError) {
        throw ownedRoomsError
      }

      // Fetch room IDs where the user is a participant
      const { data: participantRoomIds, error: participantRoomsError } =
        await supabase
          .from('room_participants')
          .select('room_id')
          .match({ user_id: userId })

      if (participantRoomsError) {
        throw participantRoomsError
      }

      // Convert participant room IDs to a Set for efficient lookups
      const participantRoomIdSet = new Set(
        participantRoomIds.map((room) => room.room_id),
      )

      // Combine owned chat rooms and participant chat rooms, avoiding duplicates
      const combinedRooms = ownedChatRooms.filter(
        (room) => !participantRoomIdSet.has(room.id),
      )
      const participantRooms = await fetchRoomsByIds([...participantRoomIdSet])
      return await getRooms([...combinedRooms, ...participantRooms], userId)
    } catch (error) {
      console.error('Error fetching specific room:', error)
    }
  }

  // Improved getRooms function
  const getRooms = async (roomIds, userId) => {
    try {
      // Fetch room participants for each room ID
      const rooms = await Promise.all(
        roomIds.map(async (room) => {
          const { data, error } = await supabase
            .from('room_participants')
            .select('room_id, users(name, id, email)')
            .match({ room_id: room.id })
            .neq('user_id', userId)

          if (error) {
            throw error
          }

          // Extract users from the response
          const users = data.map((user) => user.users).flat()
          if (users.length === 0) {
            return null
          }

          // Create the chat name for the room
          const chatName = createChatName(users, userId)

          return { ...room, room_name: chatName }
        }),
      )

      return rooms?.filter((f) => f)
    } catch (error) {
      console.error('Error fetching specific room:', error)
      return []
    }
  }

  // Utility function to fetch chat rooms by an array of room IDs
  async function fetchRoomsByIds(roomIds) {
    const { data: rooms, error } = await supabase
      .from('chat_room')
      .select('*')
      .in('id', roomIds)

    if (error) {
      console.error('Error fetching rooms by IDs:', error)
      return []
    }

    return rooms
  }

  const getMessageForChat = async (room_id) => {
    try {
      const { data: message_table, error } = await supabase
        .from('message_table')
        .select('*')
        .eq('room_id', room_id)
      return message_table
    } catch (error) {
      console.log(error)
    }
  }
  const deleteMessage = async (id, roomId) => {
    const { error } = await supabase
      .from('message_table')
      .delete()
      .eq('id', id)
      .eq('room_id', roomId)
  }
  const editMessage = async (message, id, roomId) => {
    try {
      const { data, error } = await supabase
        .from('message_table')
        .update({
          content: message,
          is_edited: true,
        })
        .match({ id: id, room_id: roomId })

      if (error) {
        throw error
      }
    } catch (error) {
      console.error('Failed to update message:', error)
    }
  }

  const getCompanyUserData = async (company_id) => {
    let { data: users, error } = await supabase
      .from('users')
      .select('*')
      .eq('company_id', company_id)
    if (error) {
      console.error('Error fetching data:', error)
      return null
    }
    const promises = users?.map(async (user) => {
      const pic =
        user.avatar_url && user.avatar_url.includes('https')
          ? user.avatar_url
          : user.avatar_url
          ? await getPublicAvatarUrl(user.avatar_url)
          : null

      return {
        ...user,
        pic: pic,
      }
    })
    const usersWithAvatars = await Promise.all(promises)
    return usersWithAvatars
  }
  const getCompanyUsersData = async (company_id, room_id, userID) => {
    try {
      const response = company_id
        ? await supabase
            .from('users')
            .select('*')
            .eq('company_id', company_id)
            .neq('id', userID)
        : null
      // console.log(
      //   'companyUsersError',
      //   companyUsersError,
      //   company_id,
      //   room_id,
      //   userID,
      // )
      const { data: roomParticipants, error: roomParticipantsError } =
        await supabase
          .from('room_participants')
          .select('user_id')
          .eq('room_id', room_id)
      const companyUsers = response?.data || []
      const companyUserIds = companyUsers?.map((user) => user.id)
      const roomUserIds = roomParticipants
        ?.filter(
          (participant) => !companyUserIds?.includes(participant.user_id),
        )
        ?.map((participant) => participant.user_id)

      if (roomUserIds?.length === 0) {
        return Promise.all(
          companyUsers.map((user) =>
            getUserAvatarUrl(user).then((pic) => ({ ...user, pic })),
          ),
        )
      }

      const { data: roomUsers, error: roomUsersError } = await supabase
        .from('users')
        .select('*')
        .in('id', roomUserIds)

      const combinedUsers = Array.from(
        new Set(
          [...companyUsers, ...roomUsers]?.map((user) => JSON.stringify(user)),
        ),
      ).map((user) => JSON.parse(user))
      // console.log('roomUserIds:', roomUserIds)
      const usersWithAvatars = await Promise.all(
        combinedUsers?.map(async (user) => ({
          ...user,
          pic: await getUserAvatarUrl(user),
        })),
      )

      return usersWithAvatars
    } catch (error) {
      console.error('Error fetching data:', error)
      return null
    }
  }

  const getUserAvatarUrl = async (user) => {
    return user.avatar_url && user.avatar_url.includes('https')
      ? user.avatar_url
      : user.avatar_url
      ? await getPublicAvatarUrl(user.avatar_url)
      : null
  }

  const createNewChat = async ({ room_name, user_id, request_support }) => {
    try {
      const { data, error } = await supabase
        .from('chat_room')
        .insert([
          { room_name, user_id, request_support, last_updated: new Date() },
        ])
        .select('id')

      if (data && data.length > 0) {
        const id = data[0].id
        setRoomId(id)
        const { dataFromRoom } = await supabase
          .from('room_participants')
          .insert([{ room_id: id, user_id: user.id }])
          .select()

        return id
      }
      if (error) {
        throw error
      }
    } catch (error) {
      console.error('Error inserting data:', error)
    }
  }

  const createNewChatAndSendMessage = async (
    chatName,
    user_id,
    company_user_id,
    message,
  ) => {
    try {
      const response = await supabase.rpc('create_chat_and_add_message', {
        _room_name: chatName,
        _user_id: user_id,
        _company_user_id: company_user_id,
        _message: message,
      })
      if (response.error) throw error
      return response.data
    } catch (error) {
      throw error
    }
  }

  async function getPublicAvatarUrl(avatarPath) {
    try {
      // If avatarPath is already a full URL, return it
      if (!avatarPath || avatarPath.includes('https://') || avatarPath.includes('http://')) {
        return avatarPath || null
      }
      
      // Remove .avatar_url suffix if it exists (database has wrong format)
      // Files in storage are stored without this extension
      let normalizedPath = avatarPath.replace('.avatar_url', '')
      
      // Try to get signed URL with just the UUID (no extension)
      const { data, error } = await supabase.storage
        .from('profiles')
        .createSignedUrl(normalizedPath, 6000000)
      
      // Return null if file doesn't exist, otherwise return signed URL
      return error ? null : (data?.signedUrl || null)
    } catch (error) {
      // Silently handle all errors - avatar is optional
      return null
    }
  }
  const getImageAndNameOfUser = async (userId) => {
    const { data, error } = await supabase
      .from('users')
      .select('avatar_url, name,email')
      .eq('id', userId)
      .single()

    if (error) {
      console.error(error)
      return { avatar_url: null, name: null }
    }
    const user = data
    const pic = user.avatar_url
      ? await getPublicAvatarUrl(user.avatar_url)
      : null
    return { avatar_url: pic, name: user.name, email: user.email }
  }
  const addPeopleToChat = async (roomId, userId) => {
    const { data, error } = await supabase
      .from('room_participants')
      .insert([{ date_joined: new Date(), room_id: roomId, user_id: userId }])
      .select('*')
    return data
  }
  const usersInRoom = async (roomId) => {
    let { data: roomParticipants, error } = await supabase
      .from('room_participants')
      .select('user_id')
      .eq('room_id', roomId)
    const peopleInRoom = await roomParticipants
    return peopleInRoom
  }
  const deletePeopleInChat = async (person) => {
    const { error } = await supabase
      .from('room_participants')
      .delete()
      .eq('user_id', person)
  }
  const addMessageToChat = async (message, userId, roomId) => {
    if (!message || !userId) {
      console.error('Message content or user ID is missing')
      return
    }
    const { data, error } = await supabase.from('message_table').insert([
      {
        content: message,
        user_id: userId,
        created_at: new Date(),
        room_id: roomId,
      },
    ])
    if (error) {
      console.error('Error inserting message:', error)
    } else {
    }
  }
  const renameChat = async (roomId, newRoomName) => {
    try {
      const { data } = await supabase
        .from('chat_room')
        .update({ room_name: newRoomName })
        .eq('id', roomId)
    } catch (error) {
      console.log('Error updating name', error)
    }
  }

  const passwordReset = async (email) => {
    const url = getURL('updatePassword')
    const response = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: url,
    })
    return response
  }

  const updatePassword = (updatedPassword) =>
    supabase.auth.updateUser({ password: updatedPassword })
  const emailExists = async (email) => {
    const { data: user, error } = await supabase
      .from('users')
      .select('email')
      .match({ email })
      .single()

    if (error) {
      return false
    }

    return user ? true : false
  }

  useEffect(() => {
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_, session) => {
      // console.log('Auth state changed', _, session)
      if (session?.access_token !== serverSession?.access_token) {
        router.refresh()
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [router, supabase, serverSession?.access_token])
  function createChatName(users, currentUserId) {
    const filteredUsers = users.filter((user) => user.id !== currentUserId)
    const userNames = filteredUsers.map(
      (user) => user.name || user.email.split('@')[0],
    )
    return userNames.join(', ')
  }

  const exposed = {
    session: serverSession,
    avatar_url,
    user,
    error,
    isLoading,
    mutate,
    signUp,
    signOut,
    signInWithGoogle,
    signInWithGoogleForVote,
    signInWithEmailPassword,
    passwordReset,
    updatePassword,
    emailExists,
    getUrl,
    addMessageToChat,
    getMessageForChat,
    createNewChat,
    createNewChatAndSendMessage,
    addPeopleToChat,
    getSpecificRoomForUser,
    roomId,
    setRoomId,
    getCompanyUserData,
    getImageAndNameOfUser,
    supabase,
    renameChat,
    roomNameNew,
    setRoomName,
    usersInRoom,
    deletePeopleInChat,
    deleteMessage,
    editMessage,
    inboxNav,
    setInboxNav,
    isNewUpdate,
    setIsisNewUpdate,
    isUserCookie,
    setUserCookie,
    admin_city,
    setAdminCity,
    navigationsItems,
    setNavigationsItems,
    selectedCompany,
    setSelectedCompany,
    selectedCompanyID,
    setSelectedCompanyID,
    joinedTeams,
    setJoinedTeams,
    getPublicAvatarUrl,
    stepContext,
    setStepContext,
    setISCompany,
    isCompany,
    setISCoreProgram,
    iSCoreProgram,
    allCompanyInfos,
    setAllCompanyInfos,
    getCompanyUsersData,
    isFirepit,
    setISFirepit,
    isAlumni,
    setISAlumni,
    currentProgram,
    setCurrentProgram,
    isInterview,
    setISInterview,
    isFurnance,
    setISFurnance,
  }

  return <Context.Provider value={exposed}>{children}</Context.Provider>
}

export const useAuth = () => {
  let context = useContext(Context)
  if (context === undefined) {
    throw new Error('useAuth must be used inside SupabaseAuthProvider')
  } else {
    return context
  }
}
